/**
 * TemperatureRole - Role para conversão de unidades de temperatura
 *
 * DIFERENTE das outras SimpleRoles: usa FÓRMULAS em vez de fatores.
 * Isso porque escalas de temperatura têm offsets diferentes.
 *
 * Para uso funcional, prefira importar dos pilares diretamente.
 *
 * @example
 * // OOP style
 * import { temperatureRole } from '@attrx/role-morphic/temperature';
 * temperatureRole.convert('celsius', 'fahrenheit', 0);  // 32
 *
 * // Functional style (preferido)
 * import { convertTemperature } from '@attrx/role-morphic/temperature/convert';
 * convertTemperature('celsius', 'fahrenheit', 0);  // 32
 */

import { SimpleRole, type SimpleValidationConfig } from "../../contracts";
import {
  TEMPERATURE_UNITS,
  TEMPERATURE_ALIASES,
  TEMPERATURE_CONSTANTS,
} from "./constants";

// =============================================================================
// TEMPERATURE ROLE CLASS
// =============================================================================

export class TemperatureRole extends SimpleRole {
  readonly name = "temperature";
  readonly base = "celsius";
  readonly units = TEMPERATURE_UNITS;
  readonly aliases = TEMPERATURE_ALIASES;

  // Temperatura tem validação especial: não pode ser abaixo do zero absoluto
  readonly validation: SimpleValidationConfig = {
    min: TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_CELSIUS,
    minError: "Temperature cannot be below absolute zero (-273.15°C)",
  };

  /**
   * Override validate para considerar o zero absoluto na escala correta
   */
  validate(
    variant: string,
    value: unknown
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Tipo
    if (typeof value !== "number") {
      return { valid: false, errors: ["Value must be a number"] };
    }

    // Finito
    if (!Number.isFinite(value)) {
      return { valid: false, errors: ["Value must be finite"] };
    }

    // Zero absoluto (converte para a escala atual)
    const absoluteZero = this.getAbsoluteZero(variant);
    if (value < absoluteZero) {
      errors.push(
        `Temperature cannot be below absolute zero (${absoluteZero}${this.units[variant]?.symbol || ""})`
      );
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Retorna o zero absoluto na escala especificada
   */
  private getAbsoluteZero(variant: string): number {
    switch (variant) {
      case "celsius":
        return TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_CELSIUS;
      case "fahrenheit":
        return TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_FAHRENHEIT;
      case "kelvin":
        return 0;
      case "rankine":
        return 0;
      default:
        return TEMPERATURE_CONSTANTS.ABSOLUTE_ZERO_CELSIUS;
    }
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/** Instância singleton da TemperatureRole */
export const temperatureRole = new TemperatureRole();
